// Transport-side "geometry too large to preview" placeholder contract (finding #13). A pathological
// geometry (millions of points/ordinates) would make the Oracle/MSSQL query-engine adapters build a
// multi-hundred-MB `{__jamSdoGeometry}` / `{__jamMssqlGeometry}` JSON marker that crosses IPC — an
// OOM/slowness risk. Over the byte budget the adapters emit THIS lightweight placeholder instead.
//
// It is a DISTINCT, NON-ERROR "too large" summary the renderer renders gracefully: the eager sniff
// path turns it into a header-only size summary ("MULTIPOLYGON · 128.0 MB"), and the lazy decode
// path into a terminal `too-large` result (never a decode crash, never a map attempt).
//
// Dependency-clean (bundle isolation A11/CD2): only `./decodeLimits` (pure constants) is imported —
// NO wkx / decoder / maplibre / proj4 / renderer-accessor — so the EAGER sniffer may import it. The
// electron adapters import the shared shape + budget from here (single source of truth), matching the
// existing marker-contract convention.

import { MAX_ONDEMAND_DECODE_BYTES } from './decodeLimits';

export type SpatialTooLargeMarker = {
  __jamSpatialTooLarge: {
    /** GeoJSON-style geom-type label (e.g. `MultiPolygon`) or `Unknown` — drives the grid glyph/summary. */
    geomType: string;
    /** Vertex/point count of the geometry the adapter declined to serialize. */
    pointCount: number;
    /** Approximate byte size of the marker that was NOT built — drives the "N MB" summary + message. */
    approxBytes: number;
  };
};

/** Transport marker byte budget — aligned with the renderer's on-demand decode ceiling
 * (`MAX_ONDEMAND_DECODE_BYTES`, 64 MB). Above it even an explicit "Load full geometry" refuses, so
 * building the full IPC marker would be pure waste + an OOM risk. */
export const MAX_SPATIAL_MARKER_BYTES = MAX_ONDEMAND_DECODE_BYTES;

export function isSpatialTooLargeMarker(value: unknown): value is SpatialTooLargeMarker {
  if (!value || typeof value !== 'object' || !('__jamSpatialTooLarge' in value)) return false;
  const inner = (value as { __jamSpatialTooLarge: unknown }).__jamSpatialTooLarge;
  return (
    !!inner &&
    typeof inner === 'object' &&
    typeof (inner as { approxBytes?: unknown }).approxBytes === 'number'
  );
}

/** Builds the placeholder marker the Oracle/MSSQL adapters emit for an over-budget geometry. */
export function makeSpatialTooLargeMarker(
  geomType: string,
  pointCount: number,
  approxBytes: number,
): SpatialTooLargeMarker {
  return { __jamSpatialTooLarge: { geomType, pointCount, approxBytes } };
}
